import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import Svg, { Circle, Text as SvgText } from 'react-native-svg';

const GratuityCalculatorScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State variables
  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);
  const [showEmployeeTypeDropdown, setShowEmployeeTypeDropdown] = useState(false);

  // Screen dimensions for responsive design
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  // Update screen dimensions on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);
  
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  
  const [formData, setFormData] = useState({
    basicSalary: '50000',
    dearnessAllowance: '5000',
    yearsOfService: '10',
    employeeType: 'covered', // 'covered' or 'not-covered'
  });

  const employeeTypes = [
    { value: 'covered', label: 'Covered under Gratuity Act' },
    { value: 'not-covered', label: 'Not covered under Gratuity Act' },
  ];

  const calculateGratuity = (showAlert = true) => {
    const basicSalary = parseFloat(formData.basicSalary) || 0;
    const dearnessAllowance = parseFloat(formData.dearnessAllowance) || 0;
    const yearsOfService = parseFloat(formData.yearsOfService) || 0;
    
    if (basicSalary <= 0 || yearsOfService <= 0) {
      if (showAlert) {
        alert('Please enter valid salary and years of service');
      }
      setResults(null);
      setIsCalculated(false);
      return;
    }
    
    const lastDrawnSalary = basicSalary + dearnessAllowance;
    let gratuityAmount = 0;
    
    if (formData.employeeType === 'covered') {
      // Formula: (n * b * 15) / 26
      gratuityAmount = (yearsOfService * lastDrawnSalary * 15) / 26;
      // Cap at 20 lakh for employees covered under Gratuity Act
      const maxGratuity = 2000000; // 20 lakh
      gratuityAmount = Math.min(gratuityAmount, maxGratuity);
    } else {
      // Formula: (15 * last drawn salary * tenure) / 30
      gratuityAmount = (15 * lastDrawnSalary * yearsOfService) / 30;
      // No cap for employees not covered under Gratuity Act
    }
    
    // Tax calculation - 20 lakh is tax-free limit, not gratuity cap
    const taxExemptLimit = 2000000; // 20 lakh
    const taxableAmount = Math.max(0, gratuityAmount - taxExemptLimit);
    const taxFreeAmount = Math.min(gratuityAmount, taxExemptLimit);
    
    const results = {
      gratuityAmount: gratuityAmount,
      taxFreeAmount: taxFreeAmount,
      taxableAmount: taxableAmount,
      lastDrawnSalary: lastDrawnSalary,
      yearsOfService: yearsOfService,
      employeeType: formData.employeeType,
      monthlyGratuityAccrual: yearsOfService > 0 ? gratuityAmount / (yearsOfService * 12) : 0,
    };
    
    setResults(results);
    setIsCalculated(true);
  };

  // Live calculation effect
  useEffect(() => {
    const basicSalary = parseFloat(formData.basicSalary) || 0;
    const yearsOfService = parseFloat(formData.yearsOfService) || 0;
    
    // Only calculate if we have valid basic inputs
    if (basicSalary > 0 && yearsOfService > 0) {
      calculateGratuity(false); // false = don't show alert for live calculation
    } else {
      setResults(null);
      setIsCalculated(false);
    }
  }, [formData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(Math.round(num));
  };

  const renderHeader = () => (
    <View style={styles.header}>
      {isMobile && (
        <TouchableOpacity 
          style={styles.hamburgerButton}
          onPress={() => setSidebarVisible(!sidebarVisible)}
        >
          <Ionicons name="menu" size={24} color={colors.text} />
        </TouchableOpacity>
      )}
      <Text style={styles.headerTitle}>Gratuity Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your gratuity amount based on your salary and years of service. 
        Get accurate gratuity calculations as per the Payment of Gratuity Act, 1972.
      </Text>
      
      <View style={styles.headerTags}>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Gratuity Act 1972</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Tax Benefits</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Retirement Planning</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>100% Free</Text>
        </View>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <Text style={styles.calculatorTitle}>Calculate Your Gratuity</Text>
      
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Basic Salary (Monthly)</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.input}
            value={formData.basicSalary}
            onChangeText={(value) => setFormData({...formData, basicSalary: value})}
            placeholder="50000"
            keyboardType="numeric"
            placeholderTextColor={colors.textSecondary}
          />
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Dearness Allowance (Monthly)</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.input}
            value={formData.dearnessAllowance}
            onChangeText={(value) => setFormData({...formData, dearnessAllowance: value})}
            placeholder="5000"
            keyboardType="numeric"
            placeholderTextColor={colors.textSecondary}
          />
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Years of Service</Text>
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.input}
            value={formData.yearsOfService}
            onChangeText={(value) => setFormData({...formData, yearsOfService: value})}
            placeholder="10"
            keyboardType="numeric"
            placeholderTextColor={colors.textSecondary}
          />
          <Text style={styles.unitText}>years</Text>
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Employee Type</Text>
        <TouchableOpacity
          style={styles.dropdown}
          onPress={() => setShowEmployeeTypeDropdown(!showEmployeeTypeDropdown)}
        >
          <Text style={styles.dropdownText}>
            {employeeTypes.find(type => type.value === formData.employeeType)?.label}
          </Text>
          <Ionicons 
            name={showEmployeeTypeDropdown ? "chevron-up" : "chevron-down"} 
            size={20} 
            color={colors.text} 
          />
        </TouchableOpacity>
        
        {showEmployeeTypeDropdown && (
          <View style={styles.dropdownOptions}>
            {employeeTypes.map((type) => (
              <TouchableOpacity
                key={type.value}
                style={styles.dropdownOption}
                onPress={() => {
                  setFormData({...formData, employeeType: type.value});
                  setShowEmployeeTypeDropdown(false);
                }}
              >
                <Text style={styles.dropdownOptionText}>{type.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) {
      return (
        <View style={styles.chartContainer}>
          <View style={styles.emptyChart}>
            <Ionicons name="pie-chart-outline" size={80} color={colors.textSecondary} />
            <Text style={styles.emptyChartText}>Calculate to see breakdown</Text>
          </View>
        </View>
      );
    }

    const total = results.gratuityAmount;
    const taxFreeAmount = results.taxFreeAmount;
    const taxableAmount = results.taxableAmount;
    
    // Calculate percentages
    const taxFreePercentage = total > 0 ? (taxFreeAmount / total) * 100 : 0;
    const taxablePercentage = total > 0 ? (taxableAmount / total) * 100 : 0;

    return (
      <View style={styles.chartContainer}>
        <Text style={styles.chartTitle}>Gratuity Breakdown</Text>
        <View style={styles.donutChart}>
          <Svg width={120} height={120}>
            {/* Background circle */}
            <Circle
              cx="60"
              cy="60"
              r="50"
              stroke="#f3f4f6"
              strokeWidth="20"
              fill="transparent"
            />
            {/* Tax Free Amount */}
            <Circle
              cx="60"
              cy="60"
              r="50"
              stroke="#10b981"
              strokeWidth="20"
              fill="transparent"
              strokeDasharray={`${(taxFreePercentage / 100) * 314.16} 314.16`}
              strokeDashoffset="0"
              transform="rotate(-90 60 60)"
            />
            {/* Taxable Amount */}
            {taxableAmount > 0 && (
              <Circle
                cx="60"
                cy="60"
                r="50"
                stroke="#f59e0b"
                strokeWidth="20"
                fill="transparent"
                strokeDasharray={`${(taxablePercentage / 100) * 314.16} 314.16`}
                strokeDashoffset={`-${(taxFreePercentage / 100) * 314.16}`}
                transform="rotate(-90 60 60)"
              />
            )}
            {/* Center text */}
            <SvgText
              x="60"
              y="55"
              textAnchor="middle"
              fontSize="12"
              fill={colors.text}
              fontWeight="600"
            >
              Total
            </SvgText>
            <SvgText
              x="60"
              y="70"
              textAnchor="middle"
              fontSize="10"
              fill={colors.textSecondary}
            >
              {formatCurrency(total).replace('₹', '₹')}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#10b981' }]} />
            <Text style={styles.legendText}>Tax Free ({taxFreePercentage.toFixed(1)}%)</Text>
          </View>
          {taxableAmount > 0 && (
            <View style={styles.legendItem}>
              <View style={[styles.legendColor, { backgroundColor: '#f59e0b' }]} />
              <Text style={styles.legendText}>Taxable ({taxablePercentage.toFixed(1)}%)</Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) {
      return (
        <View style={styles.resultsContainer}>
          <View style={styles.emptyResults}>
            <Ionicons name="calculator-outline" size={60} color={colors.textSecondary} />
            <Text style={styles.emptyResultsText}>
              Enter your details and calculate to see your gratuity amount
            </Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.resultsContainer}>
        <View style={[styles.resultCard, styles.primaryCard]}>
          <Text style={styles.resultTitle}>Total Gratuity Amount</Text>
          <Text style={[styles.resultAmount, styles.primaryAmount]}>
            {formatCurrency(results.gratuityAmount)}
          </Text>
        </View>

        <View style={styles.resultCard}>
          <Text style={styles.resultTitle}>Tax Free Amount</Text>
          <Text style={styles.resultAmount}>
            {formatCurrency(results.taxFreeAmount)}
          </Text>
        </View>

        <View style={styles.resultCard}>
          <Text style={styles.resultTitle}>Taxable Amount</Text>
          <Text style={styles.resultAmount}>
            {formatCurrency(results.taxableAmount)}
          </Text>
        </View>

        <View style={styles.resultCard}>
          <Text style={styles.resultTitle}>Monthly Accrual</Text>
          <Text style={styles.resultAmount}>
            {formatCurrency(results.monthlyGratuityAccrual)}
          </Text>
        </View>
      </View>
    );
  };

  const renderGratuityInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>Understanding Gratuity Calculator</Text>
      
      <View style={styles.infoContent}>
        <View style={styles.infoSection}>
          <Text style={styles.infoSectionTitle}>What is Gratuity?</Text>
          <Text style={styles.infoText}>
            Gratuity is a monetary benefit paid by an employer to an employee as a token of appreciation for their long-term service to the organization. It is governed by the Payment of Gratuity Act, 1972, which ensures standardized regulations across industries in India.
          </Text>
        </View>

        <View style={styles.infoSection}>
          <Text style={styles.infoSectionTitle}>Benefits of Gratuity:</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Financial Security:</Text> Provides crucial financial support after retirement, helping employees cover expenses like healthcare and other post-retirement needs</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Employee Loyalty:</Text> Demonstrates that employers value their employee's contributions and long-term financial well-being, fostering loyalty</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Tax Benefits:</Text> Gratuity is tax-free up to ₹20 lakh, offering employees significant tax savings</Text>
          <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Legal Protection:</Text> The Payment of Gratuity Act, 1972, ensures employees' right to gratuity, guaranteeing payment and preventing denial by employers</Text>
        </View>

        <View style={styles.infoSection}>
          <Text style={styles.infoSectionTitle}>Eligibility Criteria:</Text>
          <Text style={styles.bulletPoint}>• Must have completed at least 5 years of continuous service with the same employer</Text>
          <Text style={styles.bulletPoint}>• Must work in an establishment with at least 10 employees</Text>
          <Text style={styles.bulletPoint}>• Applies to both public and private sector organizations</Text>
          <Text style={styles.bulletPoint}>• In case of death or disability, gratuity is paid even without completing 5 years of service</Text>
        </View>

        <View style={styles.tipBox}>
          <Ionicons name="bulb-outline" size={20} color="#f59e0b" />
          <Text style={styles.tipText}>
            <Text style={styles.boldText}>Pro Tip:</Text> As per a Madras High Court ruling, an employee who has completed 240 days of service in the 5th year may be eligible for gratuity.
          </Text>
        </View>

        <View style={styles.infoSection}>
          <Text style={styles.infoSectionTitle}>Gratuity Calculation Formula:</Text>
          <Text style={styles.infoText}>
            <Text style={styles.boldText}>For employees covered under the Gratuity Act:</Text>
          </Text>
          <Text style={styles.infoText}>
            Gratuity = (Last drawn salary × 15 × Number of years of service) ÷ 26
          </Text>
          <Text style={styles.infoText}>
            <Text style={styles.boldText}>For employees not covered under the Gratuity Act:</Text>
          </Text>
          <Text style={styles.infoText}>
            Gratuity = (15 × Last drawn salary × Years of service) ÷ 30
          </Text>
          <Text style={styles.infoText}>
            <Text style={styles.boldText}>Where:</Text>
          </Text>
          <Text style={styles.bulletPoint}>• Last drawn salary = Basic salary + Dearness allowance</Text>
          <Text style={styles.bulletPoint}>• 15/26 represents 15 working days out of 26 working days in a month</Text>
          <Text style={styles.bulletPoint}>• Any service exceeding 6 months in the final year is rounded up to a full year</Text>
        </View>

        <View style={styles.infoSection}>
          <Text style={styles.infoSectionTitle}>How to Use This Calculator:</Text>
          <Text style={styles.bulletPoint}>1. Enter your monthly basic salary</Text>
          <Text style={styles.bulletPoint}>2. Add your dearness allowance (if applicable)</Text>
          <Text style={styles.bulletPoint}>3. Input your total years of service</Text>
          <Text style={styles.bulletPoint}>4. Select whether you're covered under the Gratuity Act</Text>
          <Text style={styles.bulletPoint}>5. The results will update automatically as you enter your information</Text>
        </View>

        <View style={styles.tipBox}>
          <Ionicons name="shield-checkmark-outline" size={20} color="#10b981" />
          <Text style={styles.tipText}>
            <Text style={styles.boldText}>Important:</Text> The maximum gratuity amount is capped at ₹20 lakh for private sector employees. Any amount exceeding this limit is treated as ex-gratia.
          </Text>
        </View>
      </View>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqSection}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.text} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>1. What is gratuity and who is eligible for it?</Text>
            <Text style={styles.faqAnswer}>
              Gratuity is a lump sum payment made by an employer to an employee as a gesture of gratitude for their service. To be eligible, an employee must have completed at least 5 years of continuous service with the same employer. However, this condition is waived in case of death or disability.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>2. How is gratuity calculated in India?</Text>
            <Text style={styles.faqAnswer}>
              For employees covered under the Gratuity Act: Gratuity = (Last drawn salary × 15 × Years of service) ÷ 26. For employees not covered: Gratuity = (15 × Last drawn salary × Years of service) ÷ 30. The last drawn salary includes basic salary and dearness allowance.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>3. What is the maximum gratuity amount I can receive?</Text>
            <Text style={styles.faqAnswer}>
              Under the Payment of Gratuity Act, the maximum gratuity amount an employee can receive is ₹20 lakhs for private sector employees. For central government employees, the cap is ₹25 lakhs. Any amount exceeding this limit is treated as ex-gratia.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>4. Is gratuity taxable?</Text>
            <Text style={styles.faqAnswer}>
              Gratuity is tax-exempt for government employees. For non-government employees, gratuity is tax-free up to ₹20 lakhs. Any gratuity received above this limit will be taxable as per your income tax slab.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>5. Can I claim gratuity before completing 5 years of service?</Text>
            <Text style={styles.faqAnswer}>
              Generally, you need to complete 5 years of continuous service to be eligible for gratuity. However, you can claim gratuity before completing 5 years only in the event of death or disability due to illness or accident. Additionally, as per a Madras High Court ruling, an employee who has completed 240 days in the 5th year may be eligible.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>6. What does 15/26 mean in gratuity calculation?</Text>
            <Text style={styles.faqAnswer}>
              In gratuity calculation, 15/26 is a standard factor that means 15 days' salary for every completed year of service, divided by 26 working days in a month. This fraction ensures a fair calculation considering only actual working days and not total calendar days.
            </Text>
          </View>

          <View style={styles.faqItem}>
             <Text style={styles.faqQuestion}>7. Can an employer deny or forfeit gratuity?</Text>
             <Text style={styles.faqAnswer}>
               Yes, an employer can forfeit gratuity if the employee's services are terminated due to disorderly conduct, violence, or any offense related to immorality during employment, provided a proper inquiry is conducted and the employee is found guilty.
             </Text>
           </View>

           <View style={styles.faqItem}>
             <Text style={styles.faqQuestion}>8. How long does it take for an employer to pay gratuity?</Text>
             <Text style={styles.faqAnswer}>
               According to the law, the employer must pay gratuity within 30 days from the date it becomes due. If the employer fails to pay within this period, they are required to pay interest on the delayed amount until it is settled.
             </Text>
           </View>

           <View style={styles.faqItem}>
             <Text style={styles.faqQuestion}>9. What happens to gratuity in case of employee death?</Text>
             <Text style={styles.faqAnswer}>
               In case of an employee's death, gratuity is paid to the nominated person or legal heirs. The gratuity is calculated based on completed years of service up to the date of death, and any service exceeding six months is rounded up to the next full year. The gratuity received by nominee/legal heir is tax-exempt up to the specified government limit.
             </Text>
           </View>

           <View style={styles.faqItem}>
             <Text style={styles.faqQuestion}>10. Which establishments are covered under the Gratuity Act?</Text>
             <Text style={styles.faqAnswer}>
               The Payment of Gratuity Act, 1972 is applicable to all establishments that employ 10 or more people. This includes various sectors such as NGOs, educational institutions, hospitals, and private companies. Once an establishment is covered by the Act, it remains applicable even if the number of employees drops below 10.
             </Text>
           </View>

           <View style={styles.faqItem}>
             <Text style={styles.faqQuestion}>11. How should I invest my gratuity amount?</Text>
             <Text style={styles.faqAnswer}>
               Consider investing your gratuity in tax-efficient instruments like Fixed Deposits for safety, PPF for long-term tax benefits, EPF for retirement savings, NPS for pension planning, or equity mutual funds for potentially higher returns. Choose based on your risk appetite and investment horizon.
             </Text>
           </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>12. Is this gratuity calculator accurate?</Text>
            <Text style={styles.faqAnswer}>
              Yes, our gratuity calculator uses the official formulas as per the Payment of Gratuity Act, 1972. It provides accurate calculations for both employees covered and not covered under the Act. However, for complex cases or specific company policies, it's advisable to consult with your HR department or a financial advisor.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      padding: 20,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    hamburgerButton: {
      marginBottom: 16,
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 16,
    },
    headerTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    headerTag: {
      backgroundColor: colors.primary,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    headerTagText: {
      color: colors.primaryText,
      fontSize: 12,
      fontWeight: '500',
    },
    content: {
      flexDirection: 'row',
      flex: 1,
    },
    leftPanel: {
      flex: 1,
      padding: 20,
    },
    rightPanel: {
      flex: 1,
      padding: 20,
    },
    calculatorContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    calculatorTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '500',
      color: colors.text,
      marginBottom: 8,
    },
    input: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: colors.text,
      backgroundColor: colors.background,
      flex: 1,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.text,
      paddingLeft: 12,
      paddingRight: 8,
    },
    dropdown: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    dropdownButton: {
      padding: 12,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    dropdownButtonText: {
      fontSize: 16,
      color: colors.text,
    },
    dropdownList: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      backgroundColor: colors.surface,
    },
    dropdownItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    dropdownItemText: {
      fontSize: 16,
      color: colors.text,
    },
    calculateButton: {
      backgroundColor: colors.primary,
      borderRadius: 8,
      padding: 16,
      alignItems: 'center',
      marginTop: 10,
    },
    calculateButtonText: {
      color: colors.primaryText,
      fontSize: 16,
      fontWeight: 'bold',
    },
    chartContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      alignItems: 'center',
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    chartTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    chartLegend: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: 20,
      gap: 20,
    },
    legendContainer: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      width: '100%',
      marginTop: 20,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 16,
      height: 16,
      borderRadius: 8,
      marginBottom: 4,
    },
    legendText: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    legendValue: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      textAlign: 'center',
    },
    resultsContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    emptyResults: {
      alignItems: 'center',
      paddingVertical: 40,
    },
    emptyResultsText: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
      marginTop: 16,
      lineHeight: 24,
    },
    resultCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      marginBottom: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    primaryCard: {
      backgroundColor: colors.primaryLight,
      borderColor: colors.primary,
    },
    resultTitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 8,
      fontWeight: '500',
    },
    resultAmount: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
    },
    primaryAmount: {
      fontSize: 24,
      color: colors.primary,
    },
    resultsTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 16,
      color: colors.textSecondary,
      flex: 1,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      textAlign: 'right',
    },
    totalResultItem: {
      backgroundColor: colors.primaryLight,
      marginTop: 8,
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderRadius: 8,
      borderBottomWidth: 0,
    },
    totalResultLabel: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.primary,
    },
    totalResultValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.primary,
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginVertical: 20,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoContent: {
      gap: 16,
    },
    infoSection: {
      marginBottom: 20,
    },
    infoSectionTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 8,
    },
    boldText: {
      fontWeight: 'bold',
      color: colors.text,
    },
    tipBox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: colors.primaryLight,
      padding: 16,
      borderRadius: 8,
      marginTop: 16,
      gap: 12,
    },
    tipText: {
      fontSize: 14,
      color: colors.text,
      lineHeight: 20,
      flex: 1,
    },
    bulletPoint: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginLeft: 16,
      marginBottom: 4,
    },
    fullWidthSection: {
      backgroundColor: colors.surface,
      padding: 20,
      marginTop: 20,
    },
    faqContainer: {
      backgroundColor: colors.surface,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      padding: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 20,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 15,
      color: colors.textSecondary,
      lineHeight: 22,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderGratuityInformation()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQSection()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default GratuityCalculatorScreen;